"use client"

import React, { ReactNode, useMemo } from "react"
import InputAdornment from "@mui/material/InputAdornment"
import { styled } from "@mui/material/styles"
import TextField from "@mui/material/TextField"
import clsx from "clsx"
import { CountryCode, getCountries } from "libphonenumber-js"
import {
  NumericFormat,
  NumericFormatProps,
  PatternFormat,
  PatternFormatProps,
} from "react-number-format"
import PhoneInput from "react-phone-number-input"
// @ts-expect-error react-phone-number-input is not typed
import cs from "react-phone-number-input/locale/cz"
// @ts-expect-error react-phone-number-input is not typed
import de from "react-phone-number-input/locale/de"
// @ts-expect-error react-phone-number-input is not typed
import en from "react-phone-number-input/locale/en"
// @ts-expect-error react-phone-number-input is not typed
import no from "react-phone-number-input/locale/nb"
// @ts-expect-error react-phone-number-input is not typed
import pl from "react-phone-number-input/locale/pl"
// @ts-expect-error react-phone-number-input is not typed
import ru from "react-phone-number-input/locale/ru"
// @ts-expect-error react-phone-number-input is not typed
import sk from "react-phone-number-input/locale/sk"
// @ts-expect-error react-phone-number-input is not typed
import sv from "react-phone-number-input/locale/sv"
// @ts-expect-error react-phone-number-input is not typed
import ua from "react-phone-number-input/locale/ua"
import { Absolute } from "ui/components/atoms/_atoms"
import Icon from "ui/components/atoms/icon"
import Spinner from "ui/components/atoms/spinner"
import { Label, P, PProps } from "ui/components/atoms/typography"
import { baseTheme } from "ui/theme"
import { BaseStyles } from "ui/types/base-styles"
import { generateStylesFromProps } from "ui/utils/styles"

import styles from "./Input.module.css"

const StyledTextField = styled(TextField)(() => {
  return {
    "width": "100%",
    "background": "none",
    ".MuiInputBase-root": {
      "boxSizing": "border-box",
      "background": "none",
      "paddingLeft": baseTheme.font.XS,
      "paddingTop": baseTheme.font.XS,
      "paddingBottom": baseTheme.font.XS,
      "borderRadius": baseTheme.variable.borderRadius,

      "&:before, &:after": {
        display: "none",
      },

      "input, textarea": {
        boxSizing: "border-box",
        color: baseTheme.color.second,
        fontSize: baseTheme.font.L,
        lineHeight: baseTheme.font.XL,
        fontWeight: baseTheme.font.light,
        padding: "0",
        paddingRight: "10px",
      },
      "input": {
        "height": 25,

        "&::placeholder": {
          color: `${baseTheme.color.third} !important`,
          opacity: 1,
        },
      },
    },
    ".MuiInputAdornment-root": {
      margin: "0 !important",
      fontSize: baseTheme.font.L,
      lineHeight: baseTheme.font.XL,
      fontWeight: baseTheme.font.light,

      p: {
        fontSize: baseTheme.font.L,
        lineHeight: baseTheme.font.XL,
        fontWeight: baseTheme.font.light,
      },
    },
  }
})

export type PhoneNumberProps = {
  lang: string
}

export type InputProps = BaseStyles & {
  elementType?: "text" | "pattern" | "numeric" | "phone"
  type?: "text" | "password"
  label?: string
  defaultValue?: string | number
  value?: string | number
  icon?: string
  name: string
  width?: unknown
  placeholder?: string
  background?: string
  error?: {
    type?: string
    message?: string
  }
  loading?: boolean
  success?: boolean
  disabled?: boolean
  format?: string
  formattedValue?: boolean
  decimalScale?: number
  decimalSeparator?: string
  allowNegative?: boolean
  endAdornment?: string
  startAdornment?: string
  textAlign?: string
  onChange?: (e: { name: string; value: any }) => void
  rhf?: object & { ref?: React.Ref<any> }
  multiline?: boolean
  rows?: number
  maxRows?: number
  inputParams?: any
  phoneOptions?: PhoneNumberProps
}

const PatterFormatCustom = React.forwardRef<
  PatternFormatProps,
  {
    onChange: (event: { target: { name: string; value: string } }) => void
    name: string
    value: string | number
    format: string
    formattedValue: boolean
  }
>(function PatterFormatCustom(props, ref) {
  const { onChange = () => {}, name, value, format, formattedValue, ...other } = props

  return (
    <PatternFormat
      {...other}
      getInputRef={ref}
      value={value || ""}
      onValueChange={values => {
        onChange({
          target: {
            name: name,
            value: (formattedValue ? values.formattedValue : values.value) as string,
          },
        })
      }}
      format={format}
    />
  )
})

const NumericFormatCustom = React.forwardRef<
  NumericFormatProps,
  {
    onChange: (event: { target: { name: string; value: string } }) => void
    name: string
    value: string | number
    decimalScale?: number
    decimalSeparator?: string
    allowNegative?: boolean
  }
>(function NumericFormatCustom(props, ref) {
  const { name, value, onChange = () => {}, ...other } = props

  return (
    <NumericFormat
      {...other}
      getInputRef={ref}
      value={value || ""}
      onValueChange={values => {
        onChange({
          target: {
            name: name,
            value: (values?.floatValue ?? "") as string,
          },
        })
      }}
    />
  )
})

function getValidCountryCode(code?: string): CountryCode {
  if (code && getCountries().includes(code?.toUpperCase() as CountryCode)) {
    return code.toUpperCase() as CountryCode
  }

  return "PL" as CountryCode
}

const PhoneFormatCustom = React.forwardRef<
  PhoneNumberProps,
  {
    onChange: (event: { target: { name: string; value: string } }) => void
    name: string
    value: string
    lang?: string
  }
>(function PhoneFormatCustom(props, ref) {
  const { name, value, onChange = () => {}, lang } = props

  const labels = useMemo(() => {
    switch (lang) {
      case "pl":
        return pl
      case "de":
        return de
      case "cs":
        return cs
      case "sk":
        return sk
      case "ua":
        return ua
      case "ru":
        return ru
      case "no":
        return no
      case "sv":
        return sv
      default:
        return en
    }
  }, [lang])

  return (
    <PhoneInput
      labels={labels}
      value={value || ""}
      defaultCountry={getValidCountryCode(lang)}
      onChange={e => {
        onChange({
          target: {
            name: name,
            value: e as string,
          },
        })
      }}
      international
      withCountryCallingCode
      countryCallingCodeEditable={false}
    />
  )
})

export const ErrorMessage = ({
  children,
  className = "",
  style = {},
  ...rest
}: {
  children?: ReactNode
  className?: string
  style?: React.CSSProperties
} & PProps) => {
  const generatedStyles = generateStylesFromProps(rest)

  const errorClasses = clsx("form_error_message", styles.errorMessage, className)
  const errorStyles = {
    ...generatedStyles,
    ...style,
  }

  return (
    <P className={errorClasses} style={errorStyles}>
      {children}
    </P>
  )
}

const Input = ({
  elementType = "text",
  type = "text",
  label,
  defaultValue,
  value,
  name,
  icon,
  error,
  placeholder,
  loading,
  success,
  disabled,
  startAdornment,
  endAdornment,
  format = "+48 ### ### ###",
  formattedValue = false,
  decimalScale = 2,
  decimalSeparator = ",",
  allowNegative = false,
  onChange = () => {},
  rhf = {},
  multiline,
  phoneOptions,
  rows,
  maxRows,
  inputParams,
  $background = "none",
  $borderColor,
  $width = "100%",
  className = "",
  style = {},
  ...rest
}: InputProps & { className?: string; style?: React.CSSProperties }) => {
  const { ref, ...rhfRest } = rhf
  const generatedStyles = generateStylesFromProps({ ...rest })
  const inputStyles = generateStylesFromProps({ $borderColor, $background })

  const handleOnChange = (e: any) => {
    onChange({
      name,
      value: e.target.value,
    })
  }

  const wrapperClasses = clsx(
    styles.wrapper,
    {
      [styles.disabled]: disabled,
    },
    className,
  )

  const wrapperStyles = {
    ...generatedStyles,
    ...style,
  }

  const boxClasses = clsx(styles.styledBox, {
    [styles.withIcon]: !!icon,
  })

  return (
    <div className={wrapperClasses} style={wrapperStyles}>
      {label && (
        <Label $type="secondary" $marginBottom="XS">
          {label}
        </Label>
      )}
      <div className={boxClasses}>
        <StyledTextField
          inputRef={ref}
          onChange={handleOnChange}
          value={value}
          error={!!error}
          disabled={disabled}
          placeholder={placeholder}
          variant="outlined"
          type={type}
          InputProps={{
            style: inputStyles,
            inputComponent:
              elementType === "numeric"
                ? (NumericFormatCustom as any)
                : elementType === "pattern"
                  ? (PatterFormatCustom as any)
                  : elementType === "phone"
                    ? (PhoneFormatCustom as any)
                    : undefined,
            inputProps: {
              ref,
              decimalScale,
              decimalSeparator,
              allowNegative,
              format,
              formattedValue,
              value,
              ...phoneOptions,
              ...inputParams,
              ...rhfRest,
            },
            startAdornment: startAdornment && (
              <InputAdornment position="start">{startAdornment}</InputAdornment>
            ),
            endAdornment: !loading && endAdornment && (
              <InputAdornment position="end">{endAdornment}</InputAdornment>
            ),
          }}
          multiline={multiline}
          rows={rows}
          maxRows={maxRows}
          {...inputParams}
          {...rhfRest}
        />
        {loading && (
          <div className={styles.iconWrapper}>
            <Spinner $size="15px" $borderWidth={3} />
          </div>
        )}
        {!loading && success && (
          <div className={styles.iconWrapper}>
            <Icon icon="check-circle" $color="green" $size="L" />
          </div>
        )}
        {icon && (
          <Absolute
            style={{ transform: "translateY(-50%)" }}
            $display="flex"
            $alignItems="center"
            $left={baseTheme.space.S}
            $top="50%"
          >
            <Icon $size="15px" icon={icon} />
          </Absolute>
        )}
      </div>
      {error && <ErrorMessage>{error?.message ?? "To pole jest wymagane"}</ErrorMessage>}
    </div>
  )
}

export default Input
